import { ProgramConfig, ApplicantSearchConfig, TargetFieldRef } from "./survey"

/**
 * Resolves a program's applicantPortal.loginFields to the concrete applicant-info
 * keys required at login. Returns `null` when the program has no applicantPortal
 * config (i.e. it's not using the new portal/login flow). Shared by backend
 * login/import logic and the public-config exposure so they never drift — pass
 * the whole program config so callers don't repeat the traversal.
 */
export function getPortalLoginKeys(config: ProgramConfig): string[] | null {
  const portal = config.applicantPortal;
  if (!portal) return null;
  return portal.loginFields === 'Email only' ? ['email'] : ['email', 'birth_date'];
}

/**
 * The target fields an applicant may authenticate with. Defaults to email, so the common case needs no
 * configuration; a program allowing another contact must list it. Never null.
 */
export function getLoginContactFields(config: ProgramConfig) {
  return config.application?.loginContactFields ?? [{ field: 'email' as TargetFieldRef }];
}

/**
 * Builds the public-facing `applicantPortal` object exposed on the config (or
 * undefined when the program isn't using the new portal). Shared by
 * getAuthAwareConfig and the distro-editor preview so the new-portal chrome
 * gate resolves identically from published config and in-progress edits.
 */
export function getPublicApplicantPortal(config: ProgramConfig) {
  const portalLoginKeys = getPortalLoginKeys(config);
  return portalLoginKeys ? {
    showToApplicants: config.applicantPortal?.showToApplicants ?? false,
    loginFieldKeys: portalLoginKeys,
    loginDescription: config.applicantPortal?.loginDescription,
    loginDisclaimer: config.applicantPortal?.loginDisclaimer,
    logoutInactiveApplicant: config.applicantPortal?.logoutInactiveApplicant,
  } : undefined;
}

export type UserToken = {
  kind: 'auth',
  deployment: string,
  user: string,
  perms: string[],
  pw?: 'ok'
  session_id?: string,
}
type Token = UserToken & { time?: Date, token?: string, expiresIn?: [number, "days" | "hours"] };

export function getAuthAwareConfig(config: ProgramConfig, token: UserToken | undefined | null) {
  const hasAuthToken = token?.kind === 'auth';
  const perms = token?.perms;
  const isAdmin = perms?.includes('admin');
  const isAdminPanel = perms?.includes('admin-panel');
  const isUserManager = perms?.includes('user_manager');

  return {
    name: config.name,
    hasAuthToken,
    interface: {
      version: config.interface?.version,
      applicantFacingLogo: config.interface?.applicantFacingLogo,
      applicantBanner: config.interface?.applicantBanner,
      staffBanner: config.interface?.staffBanner,
      loginBanner: config.interface?.loginBanner,
      cookieBanner: config.interface?.cookieBanner,
      disableReassignScreener: config.interface?.disableReassignScreener,
      sectionBottomButtons: config.interface?.sectionBottomButtons,
      includeProtectedSearchInNav: config.interface?.includeProtectedSearchInNav,
      useNewDashboards: config.interface?.useNewDashboards,
      defaultPieChartStyle: config.interface?.defaultPieChartStyle,
      translationService: config.comms?.translationService ? true : false,
      ...(hasAuthToken ? { conferenceNumbers: config.comms?.conferenceNumbers } : {}),
      disableVoiceover: config.interface?.disableVoiceover,
      applicantFacingFooter: config.interface?.applicantFacingFooter,
      applicantFacingHeader: config.interface?.applicantFacingHeader,
      applicantDisplayName: config.interface?.applicantDisplayName,
    },
    experimental: {
      enableQuickJS: config.experimental?.enableQuickJS,
      enableOfflineMode: config.experimental?.enableOfflineMode,
      extendedSpeechLength: config.experimental?.extendedSpeechLength,
      enableRoboscreenerValidateCleanUploads: config.experimental?.enableRoboscreenerValidateCleanUploads,
      newNavigationButtonStyle: config.experimental?.newNavigationButtonStyle,
      lateralUnnestingInDashboards: config.experimental?.lateralUnnestingInDashboards,
      enableCustomQueryFiltering: config.experimental?.enableCustomQueryFiltering,
      instantLoadDashboards: config.experimental?.instantLoadDashboards,
      macros: false,
      distroBreadcrumbs: config.experimental?.distroBreadcrumbs,
      showLastUpdatedOnDashboards: config?.experimental?.showLastUpdatedOnDashboards,
      enableApplicantPortal: config.experimental?.enableApplicantPortal || config.applicantPortal?.showToApplicants,
      enableAIReviewGeneration: config.experimental?.enableAIReviewGeneration,
      distroDisplayStacked: config.experimental?.distroDisplayStacked,
      helpButton: config.experimental?.helpButton,
      livenessSameTab: config.experimental?.livenessSameTab,
      enableAICoach: config.experimental?.enableAICoach,
      enableLastModifiedInJS: config.experimental?.enableLastModifiedInJS,
      ...(( isAdmin || isAdminPanel || isUserManager) ? { userManagementConfig: config.experimental?.userManagementConfig } : {}),
      ...(( isAdmin ) ? { includeLinkToOrgManagementPage: true } : {}),
      enableModernSupportTable: config.experimental?.enableModernSupportTable,
      enableDragAndDropGlobally: config.experimental?.enableDragAndDropGlobally,
      hideErrorToasts: config.experimental?.hideErrorToasts,
      requireReviewWhenNotifsChange: config.experimental?.requireReviewWhenNotifsChange,
    },
    application: {
      login: {
        logoutRedirectPath: config.application?.login?.logoutRedirectPath
      },
      guard: config.application?.guard,
    },
    applicantPortal: getPublicApplicantPortal(config),
    logOutInactiveUser: {
      timeout: config.logOutInactiveUser?.timeout,
      promptBeforeIdle: config.logOutInactiveUser?.promptBeforeIdle,
    },
    adminPasswords: config.adminPasswords ? true : false,
    adminPasskeys: (config.adminPasskeys || config.mandatoryPasskeys) ? true : false,
    mandatoryPasskeys: config.mandatoryPasskeys ? true : false,
    adminPanelEnabled: config.adminPanelEnabled ? true : false,
    ...(hasAuthToken ? {
      capabilities: {
        addApplicant: config.capabilities?.addApplicant,
        // deprecated, use applicantSearchConfig instead
        applicantSearch: config.capabilities?.applicantSearch,
        // Convert applicantSearch to record type if it's an array
        applicantSearchConfig: getApplicantSearchConfig(config),
        powerSearch: config.capabilities?.powerSearch,
        customButtons: (config.capabilities?.customButtons || []).filter(button => userHasAnyAuthorizedTag(token, button.limitedToTags || [])),
        importKeyId: config.capabilities?.importKeyId,
        thirdPartyCheckStatusTab: config.capabilities?.thirdPartyCheckStatusTab,
        aiCoach: getAIConfig(config, 'aiCoach'),
        appReviewAssistant: getAIConfig(config, 'appReviewAssistant'),
        aiSupportDraft: getAIConfig(config, 'aiSupportDraft')
      },
      comms: {
        replyScheduling: config.comms?.replyScheduling,
        gatewayConfig: config.comms?.default.smsGatewayConfig,
        twilioConfig: config.comms?.default.twilioConfig,
        videoCalling: {
          ...(config.comms?.videoCalling || {}),
          // support the deprecated `enableMeetings` value
          enabled: config.comms?.enableMeetings || config.comms?.videoCalling?.enabled,
        },
        default: {
          channelName: config.comms?.default.channelName,
          limitedToTags: config.comms?.default.limitedToTags,
          supportDashboard: {
            name: config.comms?.default.supportDashboard?.name,
            relatedApplicationDetails: config.comms?.default.supportDashboard?.relatedApplicationDetails,
            limitedToTags: config.comms?.default.supportDashboard?.limitedToTags,
            applicantInfoFields: config.comms?.default.supportDashboard?.applicantInfoFields,
          },
          customSupportCaseCreation: config.comms?.default.customSupportCaseCreation
        },
        otherChannels: (config.comms?.otherChannels || []).map(channel => {
          return {
            channelName: channel.channelName,
            limitedToTags: channel.limitedToTags,
            supportDashboard: {
              name: channel.supportDashboard?.name,
              relatedApplicationDetails: channel.supportDashboard?.relatedApplicationDetails,
              limitedToTags: channel.supportDashboard?.limitedToTags,
              applicantInfoFields: channel.supportDashboard?.applicantInfoFields,
            },
            customSupportCaseCreation: channel.customSupportCaseCreation
          }
        }),
        showRedactedMessages: config.comms?.showRedactedMessages,
        showAllContactComms: config.comms?.showAllContactComms,
      },
      payments: {
        failureHandling: config.payments?.failure_handling
      }
    } : { capabilities: { aiCoach: getAIConfig(config, 'aiCoach') } } as {} )
  }
}

export function getApplicantSearchConfig(config: ProgramConfig): ApplicantSearchConfig | undefined {
  if (Array.isArray(config.capabilities?.applicantSearch)) {
    return { kind: 'Applicant Search Options', allowedUsers: config.capabilities?.applicantSearch, searchByContact: { kind: 'Email and Phone' } } as ApplicantSearchConfig;
  }
  return config.capabilities?.applicantSearch
}

/**
* Get the AI coach config with potentially sensitive items omitted
* @param config
* @returns 
*/
export function getAIConfig(config: ProgramConfig, mode: 'aiCoach' | 'appReviewAssistant' | 'aiSupportDraft') { 
  const aiConfig = config.capabilities?.[mode];
  if (!aiConfig) {
    return;
  }
  return {
    disabled: aiConfig.disabled,
    enableVoice: aiConfig.enableVoice,
    appearance: aiConfig.appearance,
    limitToSubsurveys: aiConfig.limitToSubsurveys,
    allowedApplicants: aiConfig.allowedApplicants,
    termsOfService: aiConfig.termsOfService,
    quickStart: aiConfig.quickStart ? {
      ...(aiConfig.quickStart ?? {}),
      items: aiConfig.quickStart?.items?.map(item => item.question)
    } : undefined,
    applicantConversationReview: aiConfig?.applicantConversationReview
  };
}

export function getLoggedInConfig(config: ProgramConfig) {
  return {
    name: config.name,
    interface: {
      version: config.interface?.version,
      applicantFacingLogo: config.interface?.applicantFacingLogo,
      applicantBanner: config.interface?.applicantBanner,
      staffBanner: config.interface?.staffBanner,
      cookieBanner: config.interface?.cookieBanner,
      disableReassignScreener: config.interface?.disableReassignScreener,
      sectionBottomButtons: config.interface?.sectionBottomButtons,
      translationService: config.comms?.translationService ? true : false,
      conferenceNumbers: config.comms?.conferenceNumbers,
      disableVoiceover: config.interface?.disableVoiceover,
      applicantFacingFooter: config.interface?.applicantFacingFooter,
      applicantFacingHeader: config.interface?.applicantFacingHeader,
    },
    experimental: {
      enableQuickJS: config.experimental?.enableQuickJS,
      enableOfflineMode: config.experimental?.enableOfflineMode,
      extendedSpeechLength: config.experimental?.extendedSpeechLength,
      enableRoboscreenerValidateCleanUploads: config.experimental?.enableRoboscreenerValidateCleanUploads,
      newNavigationButtonStyle: config.experimental?.newNavigationButtonStyle,
      lateralUnnestingInDashboards: config.experimental?.lateralUnnestingInDashboards,
      enableCustomQueryFiltering: config.experimental?.enableCustomQueryFiltering,
      macros: false,
      distroBreadcrumbs: config.experimental?.distroBreadcrumbs,
      showLastUpdatedOnDashboards: config?.experimental?.showLastUpdatedOnDashboards,
      enableApplicantPortal: config.experimental?.enableApplicantPortal || config.applicantPortal?.showToApplicants,
      enableAIReviewGeneration: config.experimental?.enableAIReviewGeneration,
      distroDisplayStacked: config.experimental?.distroDisplayStacked,
      enableLastModifiedInJS: config.experimental?.enableLastModifiedInJS,
      experimental_audioConflictPrevention: config.experimental?.experimental_audioConflictPrevention,
    },
    comms: {
      replyScheduling: config.comms?.replyScheduling,
      requireConsentToRecordCalls: config.comms?.requireConsentToRecordCalls,
      videoCalling: {
        ...(config.comms?.videoCalling || {}),
        // support the deprecated `enableMeetings` value
        enabled: config.comms?.enableMeetings || config.comms?.videoCalling?.enabled,
      },
      default: {
        channelName: config.comms?.default.channelName,
        limitedToTags: config.comms?.default.limitedToTags,
        supportDashboard: {
          name: config.comms?.default.supportDashboard?.name,
          relatedApplicationDetails: config.comms?.default.supportDashboard?.relatedApplicationDetails,
          limitedToTags: config.comms?.default.supportDashboard?.limitedToTags,
          applicantInfoFields: config.comms?.default.supportDashboard?.applicantInfoFields,
        },
        contentTemplates: config.comms?.default.twilioConfig.whatsapp_content_templates,
      },
      otherChannels: (config.comms?.otherChannels || []).map(channel => {
        return {
          channelName: channel.channelName,
          limitedToTags: channel.limitedToTags,
          supportDashboard: {
            name: channel.supportDashboard?.name,
            relatedApplicationDetails: channel.supportDashboard?.relatedApplicationDetails,
            limitedToTags: channel.supportDashboard?.limitedToTags,
            applicantInfoFields: channel.supportDashboard?.applicantInfoFields,
          },
          contentTemplates: channel.twilioConfig.whatsapp_content_templates
        }
      }),
      showRedactedMessages: config.comms?.showRedactedMessages,
      showAllContactComms: config.comms?.showAllContactComms,
      supportTemplates: config.comms?.supportTemplates,
    },
    payments: {
      failureHandling: config.payments?.failure_handling
    },
    application: {
      login: {
        logoutRedirectPath: config.application?.login?.logoutRedirectPath
      }
    },
    capabilities: {
      addApplicant: config.capabilities?.addApplicant,
      importKeyId: config.capabilities?.importKeyId,
      copyEditPublish: config.capabilities?.copyEditPublish,
      aiCoach: config.capabilities?.aiCoach,
      aiSupportDraft: getAIConfig(config, 'aiSupportDraft'),
      noteTags: config.capabilities?.noteTags,
      activityLog: config.capabilities?.activityLog
        ? { limitedToUsersTagged: config.capabilities.activityLog.limitedToUsersTagged }
        : undefined,
      fileHub: config.capabilities?.fileHub
    },
    fraud: {
      enableTab: config.fraud?.enableTab,
    },
    accounting: {
      monthlyReconciliationReports: config.accounting?.monthlyReconciliationReports,
    },
  }
}

export type PublicConfig = ReturnType<typeof getAuthAwareConfig>
export type LoggedInConfig = ReturnType<typeof getLoggedInConfig>


export const permInToken = (perm: string, token?: Token) => token?.kind === 'auth' && token.perms.indexOf(perm) !== -1;

/** Returns true if the token has ANY permission in the limitedToUserTags array */
export const userHasAnyAuthorizedTag = (token: UserToken, limitedToUserTags: string[]) => {
  if (limitedToUserTags.length === 0) return true;
  for (const tag of limitedToUserTags) {
    if (permInToken(tag, token)) return true;
  }
  return false;
}
